import { defineStore } from "pinia";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import i18n from "@/Plugins/i18n";

import type { PrivacyState } from "./Models/State";
import type { PrivacySecurityState } from "./Models/SecurityState";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";

import {
  deletePrivacySecurityState,
  getPrivacySecurityState,
  setPrivacySecurityState,
} from "./SecureStorage";

import { LocalStorageService } from "@/Services/LocalStorageService";

import { config } from "@/Common/envConfig";

import { isApp } from "@/Utils/Ionic";
import { isValidString } from "@/Utils/String";
import { unixUTC } from "@/Utils/Date";
import { compareString, hashString } from "@/Utils/Crypto";

const privacyStorage = new LocalStorageService(LocalStorageNamespace.PRIVACY);
const MAX_PIN_ATTEMPTS = 5;
const PIN_LOCK_DURATIONS_SECONDS = [30, 60, 5 * 60, 15 * 60];
let pinOperationVersion = 0;
let pinHash: string | undefined;
let privacyInitialization: Promise<void> | undefined;

const getStoredNonNegativeInteger = (key: LocalStorageKey): number => {
  const value = Number(privacyStorage.get<unknown>(key));

  if (!Number.isInteger(value) || value < 0) {
    return 0;
  }

  return value;
};

const clearLegacySecurityState = () => {
  privacyStorage.delete(LocalStorageKey.PRIVACY_PIN_CODE);
  privacyStorage.delete(LocalStorageKey.PRIVACY_PIN_ATTEMPTS);
  privacyStorage.delete(LocalStorageKey.PRIVACY_PIN_LOCK_LEVEL);
  privacyStorage.delete(LocalStorageKey.PRIVACY_PIN_LOCK_UNTIL);
  privacyStorage.delete(LocalStorageKey.PRIVACY_DATE);
  privacyStorage.delete(LocalStorageKey.PRIVACY_BIO_CHECKED);
};

const normalizeSecurityState = (state: PrivacySecurityState): PrivacySecurityState => {
  const now = unixUTC();
  const maxPinValidUntil = now + config.privacyValidSecsCode;
  const maxPinLockUntil = now + PIN_LOCK_DURATIONS_SECONDS[PIN_LOCK_DURATIONS_SECONDS.length - 1];

  return {
    ...state,
    pinValidUntil:
      state.pinValidUntil > now && state.pinValidUntil <= maxPinValidUntil ? state.pinValidUntil : 0,
    failedPinAttempts: Math.min(state.failedPinAttempts, MAX_PIN_ATTEMPTS - 1),
    pinLockLevel: Math.min(state.pinLockLevel, PIN_LOCK_DURATIONS_SECONDS.length - 1),
    pinLockUntil: state.pinLockUntil > now ? Math.min(state.pinLockUntil, maxPinLockUntil) : 0,
  };
};

const getLegacySecurityState = async (): Promise<PrivacySecurityState | undefined> => {
  const storedPin = privacyStorage.get<unknown>(LocalStorageKey.PRIVACY_PIN_CODE);

  if (!isValidString(storedPin)) {
    return undefined;
  }

  const storedHash = storedPin.length === config.privacyCodeLength ? await hashString(storedPin) : storedPin;

  return normalizeSecurityState({
    version: 1,
    pinHash: storedHash,
    pinValidUntil: getStoredNonNegativeInteger(LocalStorageKey.PRIVACY_DATE),
    failedPinAttempts: getStoredNonNegativeInteger(LocalStorageKey.PRIVACY_PIN_ATTEMPTS),
    pinLockLevel: getStoredNonNegativeInteger(LocalStorageKey.PRIVACY_PIN_LOCK_LEVEL),
    pinLockUntil: getStoredNonNegativeInteger(LocalStorageKey.PRIVACY_PIN_LOCK_UNTIL),
  });
};

const hasStoredPrivacyHint =
  privacyStorage.get<unknown>(LocalStorageKey.PRIVACY_ENABLED_HINT) === "true" ||
  isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_PIN_CODE));

const privacyState: PrivacyState = {
  isPrivate: isApp() && hasStoredPrivacyHint,
  isInitialized: !isApp(),
  pinValidUntil: 0,
  failedPinAttempts: 0,
  pinLockLevel: 0,
  pinLockUntil: 0,
};

const getCurrentSecurityState = (state: PrivacyState): PrivacySecurityState => {
  if (!isValidString(pinHash)) {
    throw new Error("Privacy PIN is not configured");
  }

  return {
    version: 1,
    pinHash,
    pinValidUntil: state.pinValidUntil,
    failedPinAttempts: state.failedPinAttempts,
    pinLockLevel: state.pinLockLevel,
    pinLockUntil: state.pinLockUntil,
  };
};

const persistSecurityState = async (state: PrivacyState): Promise<void> => {
  await setPrivacySecurityState(getCurrentSecurityState(state));
};

export const usePrivacyStore = defineStore(StateNamespace.PRIVACY, {
  state: () => ({ ...privacyState }),
  getters: {},
  actions: {
    async clear(): Promise<void> {
      await this.setIsPrivate(false);
    },

    async initialize(): Promise<void> {
      if (this.isInitialized || !isApp()) {
        this.isInitialized = true;

        return;
      }

      if (privacyInitialization) {
        return privacyInitialization;
      }

      const operationVersion = pinOperationVersion;

      privacyInitialization = (async () => {
        const storedSecurityState = await getPrivacySecurityState();
        const securityState = storedSecurityState
          ? normalizeSecurityState(storedSecurityState)
          : await getLegacySecurityState();

        if (operationVersion !== pinOperationVersion) {
          return;
        }

        if (securityState) {
          await setPrivacySecurityState(securityState);

          if (operationVersion !== pinOperationVersion) {
            return;
          }

          pinHash = securityState.pinHash;
          this.isPrivate = true;
          this.pinValidUntil = securityState.pinValidUntil;
          this.failedPinAttempts = securityState.failedPinAttempts;
          this.pinLockLevel = securityState.pinLockLevel;
          this.pinLockUntil = securityState.pinLockUntil;

          privacyStorage.set(LocalStorageKey.PRIVACY_ENABLED_HINT, "true");
        } else {
          pinHash = undefined;
          this.isPrivate = false;
          this.pinValidUntil = 0;
          this.failedPinAttempts = 0;
          this.pinLockLevel = 0;
          this.pinLockUntil = 0;

          privacyStorage.delete(LocalStorageKey.PRIVACY_ENABLED_HINT);
        }

        clearLegacySecurityState();
        this.isInitialized = true;
      })().finally(() => {
        privacyInitialization = undefined;
      });

      return privacyInitialization;
    },

    async setIsPrivate(val: boolean): Promise<void> {
      if (val) {
        if (!isValidString(pinHash)) {
          throw new Error("Privacy PIN is not configured");
        }

        this.isPrivate = true;
        privacyStorage.set(LocalStorageKey.PRIVACY_ENABLED_HINT, "true");

        return;
      }

      pinOperationVersion++;

      if (privacyInitialization) {
        await privacyInitialization.catch(() => undefined);
      }

      await deletePrivacySecurityState();

      pinHash = undefined;
      this.isPrivate = false;
      this.isInitialized = true;
      this.pinValidUntil = 0;
      this.failedPinAttempts = 0;
      this.pinLockLevel = 0;
      this.pinLockUntil = 0;

      privacyStorage.delete(LocalStorageKey.PRIVACY_ENABLED_HINT);
      privacyStorage.delete(LocalStorageKey.PRIVACY_BIO);
      clearLegacySecurityState();
    },

    async validatePin(newPin: string): Promise<boolean> {
      await this.initialize();

      if (!this.isPrivate || this.isPinLocked()) {
        return false;
      }

      const storedHash = pinHash;
      const operationVersion = pinOperationVersion;

      if (!isValidString(storedHash)) {
        return false;
      }

      const isValid = await compareString(newPin, storedHash);
      const isCurrentOperation =
        this.isPrivate && operationVersion === pinOperationVersion && pinHash === storedHash;

      if (!isCurrentOperation) {
        return false;
      }

      if (isValid) {
        await this.resetPinAttempts();

        return true;
      }

      await this.registerFailedPinAttempt();

      return false;
    },

    async registerFailedPinAttempt(): Promise<void> {
      if (this.isPinLocked()) {
        return;
      }

      this.failedPinAttempts++;

      if (this.failedPinAttempts < MAX_PIN_ATTEMPTS) {
        await persistSecurityState(this);

        return;
      }

      const lockLevel = Math.min(this.pinLockLevel, PIN_LOCK_DURATIONS_SECONDS.length - 1);
      const lockDuration = PIN_LOCK_DURATIONS_SECONDS[lockLevel];

      this.failedPinAttempts = 0;
      this.pinLockLevel = Math.min(lockLevel + 1, PIN_LOCK_DURATIONS_SECONDS.length - 1);
      this.pinLockUntil = unixUTC() + lockDuration;

      await persistSecurityState(this);
    },

    async resetPinAttempts(): Promise<void> {
      this.failedPinAttempts = 0;
      this.pinLockLevel = 0;
      this.pinLockUntil = 0;

      await persistSecurityState(this);
    },

    getPinLockRemainingSeconds(): number {
      const remainingSeconds = this.pinLockUntil - unixUTC();

      if (remainingSeconds > 0) {
        return remainingSeconds;
      }

      if (this.pinLockUntil > 0) {
        this.pinLockUntil = 0;
      }

      return 0;
    },

    isPinLocked(): boolean {
      return this.getPinLockRemainingSeconds() > 0;
    },

    async updatePinDate(): Promise<void> {
      if (!this.isPrivate || !this.isInitialized) {
        return;
      }

      this.pinValidUntil = unixUTC() + config.privacyValidSecsCode;

      await persistSecurityState(this);
    },

    async updatePinCode(value: string, signal?: AbortSignal): Promise<void> {
      await this.initialize();

      if (signal?.aborted) {
        throw new Error("PIN update was cancelled");
      }

      const operationVersion = ++pinOperationVersion;
      const hash = await hashString(value);

      if (operationVersion !== pinOperationVersion || signal?.aborted) {
        throw new Error("PIN update was cancelled");
      }

      pinHash = hash;
      this.pinValidUntil = 0;
      this.failedPinAttempts = 0;
      this.pinLockLevel = 0;
      this.pinLockUntil = 0;

      await persistSecurityState(this);

      clearLegacySecurityState();
    },

    needProtection(): boolean {
      if (!this.isPrivate) {
        return false;
      }

      if (!this.isInitialized || !Number.isInteger(this.pinValidUntil)) {
        return true;
      }

      const now = unixUTC();

      return this.pinValidUntil <= now || this.pinValidUntil > now + config.privacyValidSecsCode;
    },

    isBioEnabled(): boolean {
      try {
        return isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_BIO));
      } catch (e) {
        return false;
      }
    },

    setIsBio(bool: boolean) {
      if (bool === false) {
        privacyStorage.delete(LocalStorageKey.PRIVACY_BIO);
      } else {
        privacyStorage.set(LocalStorageKey.PRIVACY_BIO, "true");
      }
    },

    async checkBiometric(hasFallback = false): Promise<boolean> {
      if (isApp()) {
        const t = i18n.global.t;
        const biometrics = await NativeBiometric.isAvailable().catch(() => ({ isAvailable: false }));

        if (biometrics.isAvailable) {
          const isVerified = await NativeBiometric.verifyIdentity({
            reason: t("settings.privacy.bio.reason"),
            title: t("settings.privacy.bio.title"),
            subtitle: t("settings.privacy.bio.subtitle"),
            description: t("settings.privacy.bio.description"),
            useFallback: hasFallback,
            maxAttempts: 2,
          })
            .then(() => true)
            .catch(() => false);

          return isVerified;
        }

        return false;
      }

      return false;
    },
  },
});

export type PrivacyStore = ReturnType<typeof usePrivacyStore>;

export default usePrivacyStore;
